import { useAccountingDisplayTimezone } from '../../../hooks/useAccountingDisplayTimezone';
import { useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import type {
  CountedOpeningContext,
  CountedOpeningReceipt,
  DailyCashKind,
  AccountingHistoryResponse,
} from '@pokerroom/types';
import { api } from '../../../lib/api';
import { useAuthStore, sessionVenueKey } from '../../../store/auth.store';
import { useCashIntent, useCashScope } from './useCashIntent';
import type { CashIntent } from './cashRecovery';
import { CashRecoveryNotice } from './CashRecoveryNotice';
import { formatAccountingInstant } from '../../../lib/accounting-time';
const originalKinds: DailyCashKind[] = ['ZIPA_DRAWER', 'RESERVE', 'PETTY'];
const validDay = (value: unknown): value is string =>
  typeof value === 'string' &&
  /^\d{4}-\d{2}-\d{2}$/.test(value) &&
  Number.isFinite(Date.parse(`${value}T00:00:00Z`)) &&
  new Date(`${value}T00:00:00Z`).toISOString().slice(0, 10) === value;
const money = (v: string) => /^\d{1,10}(\.\d{1,2})?$/.test(v);
// Integer cents avoid rounding a displayed variance to the wrong side of zero.
const cents = (v: string) => {
  const [whole, fraction = ''] = v.split('.');
  return BigInt(whole!) * 100n + BigInt(fraction.padEnd(2, '0'));
};
function difference(a: string, b: string) {
  const n = cents(a) - cents(b),
    abs = n < 0n ? -n : n;
  return `${n < 0n ? '-' : '+'}${abs / 100n}.${String(abs % 100n).padStart(2, '0')}`;
}
export function CountedOpeningPanel({
  blocked = false,
  onPending,
  pageHeader = false,
}: {
  blocked?: boolean;
  pageHeader?: boolean;
  onPending?: (v: boolean) => void;
}) {
  const { t, i18n } = useTranslation('accounting');
  const displayTimezone = useAccountingDisplayTimezone();
  const scope = useCashScope(),
    qc = useQueryClient();
  const venueId = useAuthStore((s) => sessionVenueKey(s.accessToken));
  const revision = useAuthStore((s) => s.sessionRevision);
  const actions = useAuthStore((s) => s.allowedActions);
  const canRead = actions?.includes('accounting:read') === true,
    canOpen = actions?.includes('accounting:open') === true;
  const cash = useCashIntent(scope, 'counted-opening');
  const [counts, setCounts] = useState<Record<string, string>>({});
  const [note, setNote] = useState(''),
    [confirmed, setConfirmed] = useState(false),
    [emptySafeConfirmed, setEmptySafeConfirmed] = useState(false),
    [lookupKey, setLookupKey] = useState('');
  const [receipt, setReceipt] = useState<CountedOpeningReceipt | null>(null);
  const context = useQuery({
    queryKey: ['counted-opening-context', scope, revision],
    enabled: canRead,
    retry: false,
    queryFn: () => api.get<CountedOpeningContext>('/accounting/counted-opening'),
    refetchOnWindowFocus: 'always',
    refetchInterval: 30_000,
  });
  const current =
    context.data?.venueId === venueId && context.data?.contractVersion === 1
      ? context.data
      : undefined;
  const sharedOpen = current?.blockers.includes('CURRENT_OPEN') === true;
  const closedDay = current?.blockers.includes('CURRENT_DAY_CLOSED') === true;
  const closing = useQuery({
    queryKey: ['closed-opening-balances', scope, revision, current?.businessDate, current?.version],
    enabled: canRead && closedDay,
    retry: false,
    refetchOnWindowFocus: 'always',
    queryFn: () =>
      api.get<AccountingHistoryResponse>('/accounting/history', {
        params: { date: current!.businessDate },
      }),
  });
  const closed =
    !closing.isError && closing.data?.venueId === venueId
      ? closing.data.days.find(
          (d) => d.businessDate === current?.businessDate && d.status === 'CLOSED',
        )
      : undefined;
  const summary = current?.openingSummary;
  const currentReceipt = receipt?.businessDate === current?.businessDate ? receipt : null;
  const verifiedRecovery = useRef('');
  useEffect(() => {
    if (!canRead || !current || cash.busy || cash.intent?.stage !== 'COMMITTED') return;
    const signature = `${scope}:${revision}:${current.businessDate}:${cash.intent.operationKey}`;
    if (verifiedRecovery.current === signature) return;
    verifiedRecovery.current = signature;
    void lookup();
  }, [
    scope,
    revision,
    canRead,
    current?.businessDate,
    cash.busy,
    cash.intent?.operationKey,
    cash.intent?.stage,
  ]);
  useEffect(() => {
    setCounts({});
    setNote('');
    setConfirmed(false);
    setEmptySafeConfirmed(false);
    setReceipt(null);
  }, [scope, revision, current?.businessDate]);
  useEffect(() => {
    setConfirmed(false);
    setEmptySafeConfirmed(false);
  }, [current?.version]);
  useEffect(() => {
    onPending?.(cash.busy || !!cash.intent);
  }, [cash.busy, cash.intent, onPending]);
  const kinds: DailyCashKind[] =
    current?.dailySettlementProtocol !== 1 && current?.cashBagContractVersion === 2
      ? [...originalKinds, 'OPERATING_REVENUE']
      : originalKinds;
  const valid = kinds.every((kind) => money(counts[kind] ?? ''));
  const different =
    valid &&
    !!current?.book &&
    kinds.some(
      (kind) =>
        cents(counts[kind]!) !== cents(current.book![kind]!) ||
        (current.reference.amounts &&
          cents(counts[kind]!) !== cents(current.reference.amounts[kind]!)),
    );
  function validate(value: CountedOpeningReceipt, key: string) {
    const receiptKinds =
      value.dailySettlementProtocol !== 1 && value.cashBagContractVersion === 2
        ? [...originalKinds, 'OPERATING_REVENUE']
        : originalKinds;
    return (
      value?.contractVersion === 1 &&
      value.authority === 'VENUE_COUNTED_V1' &&
      (value.dailySettlementProtocol !== 1 || value.countsConfirmed === true) &&
      value.venueId === venueId &&
      value.operationKey === key &&
      value.status === 'OPEN' &&
      ['REVIEW_REQUIRED', 'CONFIRMED'].includes(value.reviewStatus) &&
      typeof value.formalOpeningConfirmed === 'boolean' &&
      typeof value.reviewNote === 'string' &&
      validDay(value.businessDate) &&
      !!value.sourceWatermark &&
      !!value.openingRef &&
      !!value.sourceCutoff &&
      !!value.sessionId &&
      !!value.operationId &&
      Array.isArray(value.counts) &&
      value.counts.length === receiptKinds.length &&
      receiptKinds.every(
        (kind) =>
          value.counts.filter(
            (c) =>
              c.kind === kind &&
              money(c.amount) &&
              money(c.bookAmount) &&
              /^-?\d{1,10}\.\d{2}$/.test(c.variance) &&
              (c.comparisonVariance === null || /^-?\d{1,10}\.\d{2}$/.test(c.comparisonVariance)),
          ).length === 1,
      )
    );
  }
  async function accepted(value: CountedOpeningReceipt, record: CashIntent) {
    const confirmedRecord = {
      ...record,
      businessDate: value.businessDate,
      stage: 'COMMITTED' as const,
    };
    cash.committed(confirmedRecord); // A verified success can never be replayed as a POST.
    const latest = await cash.run(() =>
      api.get<CountedOpeningContext>('/accounting/counted-opening'),
    );
    if (!latest) return;
    if (
      latest.venueId !== venueId ||
      latest.contractVersion !== 1 ||
      !validDay(latest.businessDate) ||
      value.businessDate > latest.businessDate
    ) {
      cash.setError('RECONCILE');
      return;
    }
    qc.setQueryData(['counted-opening-context', scope, revision], latest);
    if (value.businessDate < latest.businessDate && !cash.archiveCommitted(confirmedRecord)) return;
    setReceipt(value);

    await qc.invalidateQueries({ queryKey: ['accounting-business-day'] });
    await qc.invalidateQueries({ queryKey: ['accounting-session-context'] });
    await qc.invalidateQueries({ queryKey: ['member-membership-fee'] });
    await qc.invalidateQueries({ queryKey: ['cash-context'] });
  }
  async function submit(previous?: CashIntent) {
    if (
      !canOpen ||
      (!previous &&
        (blocked ||
          cash.intent ||
          !current?.canOpen ||
          !valid ||
          !confirmed ||
          (current?.safeInitialization?.required && !emptySafeConfirmed) ||
          (different && !note.trim()) ||
          context.isFetching))
    )
      return;
    const now = Date.now(),
      key = crypto.randomUUID();
    const record: CashIntent = previous ?? {
      recordVersion: 1,
      scope,
      createdAt: now,
      lastSeenAt: now,
      action: 'OPEN_SESSION',
      operationKey: key,
      businessDate: current!.businessDate,
      stage: 'UNKNOWN',
      body: {
        operationKey: key,
        cashBagContractVersion: current!.cashBagContractVersion ?? 1,
        ...(current!.dailySettlementProtocol === 1 ? { dailySettlementProtocol: 1 as const } : {}),
        businessDate: current!.businessDate,
        expectedVersion: current!.version,
        counts: kinds.map((kind) => ({ kind, amount: counts[kind]! })),
        countsConfirmed: confirmed,
        ...(current!.safeInitialization?.required ? { emptySafeConfirmed } : {}),
        reviewNote: note,
      },
    };
    const result = await cash.post<CountedOpeningReceipt>(
      record,
      '/accounting/counted-opening',
      (v) => validate(v, record.operationKey),
    );
    if (result) await accepted(result, record);
  }
  async function lookup() {
    const key = cash.intent?.operationKey ?? lookupKey.trim();
    if (!key) return;
    const value = await cash.run(() =>
      api.get<CountedOpeningReceipt>(
        `/accounting/counted-opening/operations/${encodeURIComponent(key)}`,
      ),
    );
    if (!value) return;
    if (!validate(value, key)) {
      cash.setError('RECONCILE');
      return;
    }
    const now = Date.now();
    await accepted(
      value,
      cash.intent ?? {
        recordVersion: 1,
        scope,
        createdAt: now,
        lastSeenAt: now,
        action: 'OPEN_SESSION',
        operationKey: key,
        stage: 'COMMITTED',
      },
    );
  }
  async function readHistory(record: CashIntent) {
    const value = await cash.run(() =>
      api.get<CountedOpeningReceipt>(
        `/accounting/counted-opening/operations/${encodeURIComponent(record.operationKey)}`,
      ),
    );
    if (!value) return;
    if (!validate(value, record.operationKey)) {
      cash.setError('RECONCILE');
      return;
    }
    setReceipt(value); // Read-only; never replace the active intent or its recovery key.
  }
  if (!canRead || cash.error === 'DENIED') return <CashRecoveryNotice error="DENIED" />;
  return (
    <section
      aria-label={t('counted.title')}
      className="min-w-0 rounded-xl border border-base-300 bg-base-100 p-4 space-y-5 lg:p-6"
    >
      {!pageHeader && <h1 className="text-2xl font-bold">{t('counted.title')}</h1>}
      {current && <p>{t('counted.date', { date: current.businessDate })}</p>}
      {context.isError ? (
        <p role="alert">
          {t('counted.loadFailed')}{' '}
          <button className="btn min-h-11" onClick={() => void context.refetch()}>
            {t('cashWorkflow.refresh')}
          </button>
        </p>
      ) : !current ? (
        <p role="status">{t('cashSummaryLoading')}</p>
      ) : (
        <>
          {!sharedOpen && !closedDay && (
            <p>
              {t(`counted.reference.${current.reference.source}`, {
                date: current.reference.businessDate,
              })}
            </p>
          )}
          {current.blockers
            .filter((reason) => !sharedOpen || reason === 'CURRENT_OPEN')
            .map((reason) => (
              <p key={reason} role="status">
                {t(`counted.blockers.${reason}`, { defaultValue: t('counted.blocked') })}
              </p>
            ))}
          {closedDay && (
            <div role="status" className="rounded-lg border border-base-300 p-4 space-y-3">
              <h2 className="font-semibold">
                {t('counted.closedBalances', { date: current.businessDate })}
              </h2>
              <p>{t('counted.closedHelp')}</p>
              {closed ? (
                <dl className="grid gap-3 sm:grid-cols-2">
                  {(['ZIPA_DRAWER', 'RESERVE', 'PETTY', 'OPERATING_REVENUE'] as const).map(
                    (kind, index) => {
                      const value = [closed.drawer, closed.reserve, closed.petty, closed.revenue][
                        index
                      ];
                      return (
                        <div key={kind}>
                          <dt>{t(`cashWorkflow.bags.${kind}`)}</dt>
                          <dd className="tabular-nums">
                            {value?.value ?? t('counted.unavailable')} TWD
                          </dd>
                        </div>
                      );
                    },
                  )}
                </dl>
              ) : (
                <p>
                  {closing.isFetching ? t('cashSummaryLoading') : t('counted.closedUnavailable')}
                </p>
              )}
            </div>
          )}
          {sharedOpen && (
            <div role="status" className="rounded-lg border border-base-300 p-4 space-y-2">
              {summary?.status === 'AVAILABLE' && summary.businessDate === current.businessDate ? (
                <>
                  <h2 className="font-semibold">{t('counted.initialSnapshot')}</h2>
                  <p>
                    {t('counted.openedAt', {
                      date:
                        formatAccountingInstant(
                          summary.openedAt,
                          displayTimezone ?? '',
                          i18n.language,
                        ) ?? t('counted.unavailable'),
                    })}
                  </p>
                  <p>{t(`counted.result.${summary.reviewStatus}`)}</p>
                  <dl className="grid gap-3 sm:grid-cols-2">
                    {summary.counts.map((c) => (
                      <div key={c.kind}>
                        <dt>{t(`counted.input.${c.kind}`)}</dt>
                        <dd className="tabular-nums">{c.amount}</dd>
                        <dd className="text-sm">{t('counted.variance', { amount: c.variance })}</dd>
                        {c.comparisonVariance !== null && (
                          <dd className="text-sm">
                            {t('counted.comparisonVariance', { amount: c.comparisonVariance })}
                          </dd>
                        )}
                      </div>
                    ))}
                  </dl>
                </>
              ) : (
                <p>{t('counted.summaryUnavailable')}</p>
              )}
            </div>
          )}
          {!sharedOpen && !closedDay && !cash.intent && !currentReceipt && (
            <>
              <p className="text-sm">{t('counted.help')}</p>
              <div className="grid gap-4 md:grid-cols-2">
                {kinds.map((kind) => (
                  <label
                    key={kind}
                    className="min-w-0 rounded-lg border border-base-300 bg-base-200/50 p-4 space-y-2"
                  >
                    <span className="font-semibold">{t(`cashWorkflow.bags.${kind}`)}</span>
                    <span className="block text-sm">
                      {t(
                        current.reference.source === 'INITIAL_CONFIG'
                          ? 'counted.initialAmount'
                          : 'counted.previousCloseAmount',
                        {
                          amount: current.reference.amounts?.[kind] ?? t('counted.unavailable'),
                        },
                      )}
                      {current.book?.[kind] !== current.reference.amounts?.[kind] && (
                        <>
                          {' '}
                          ·{' '}
                          {t('counted.bookAmount', {
                            amount: current.book?.[kind] ?? t('counted.unavailable'),
                          })}
                        </>
                      )}
                    </span>
                    <input
                      aria-label={t(`counted.input.${kind}`)}
                      className="input input-bordered w-full min-h-11 rounded-md border border-base-300 px-3 py-2 text-right tabular-nums bg-base-100"
                      inputMode="decimal"
                      autoComplete="off"
                      value={counts[kind] ?? ''}
                      disabled={cash.busy || !canOpen || !current.canOpen || blocked}
                      onChange={(e) => {
                        setCounts((old) => ({ ...old, [kind]: e.target.value }));
                        setConfirmed(false);
                        setEmptySafeConfirmed(false);
                      }}
                    />
                    {money(counts[kind] ?? '') && current.book && (
                      <span className="block text-sm">
                        {t('counted.variance', {
                          amount: difference(counts[kind]!, current.book[kind]!),
                        })}
                      </span>
                    )}
                    {money(counts[kind] ?? '') && current.reference.amounts && (
                      <span className="block text-sm">
                        {t('counted.comparisonVariance', {
                          amount: difference(counts[kind]!, current.reference.amounts[kind]!),
                        })}
                      </span>
                    )}
                  </label>
                ))}
              </div>
              {different && (
                <p role="status" className="text-warning">
                  {t('counted.differenceHelp')}
                </p>
              )}
              <label className="block space-y-2">
                {t('counted.reviewNote')}
                <textarea
                  aria-label={t('counted.reviewNote')}
                  maxLength={1000}
                  className="textarea textarea-bordered w-full min-h-24 rounded-md border border-base-300 bg-base-100 px-3 py-2"
                  value={note}
                  onChange={(e) => {
                    setNote(e.target.value);
                    setConfirmed(false);
                    setEmptySafeConfirmed(false);
                  }}
                  disabled={cash.busy}
                />
              </label>
              {current.safeInitialization?.required && (
                <div className="rounded-lg border border-base-300 p-3 space-y-2">
                  <p>{t('counted.emptySafeHelp')}</p>
                  <label className="flex gap-2 min-h-11 items-center">
                    <input
                      type="checkbox"
                      checked={emptySafeConfirmed}
                      disabled={cash.busy || !canOpen || !current.canOpen || blocked}
                      onChange={(e) => setEmptySafeConfirmed(e.target.checked)}
                    />
                    {t('counted.emptySafeConfirm')}
                  </label>
                </div>
              )}
              <label className="flex gap-2 min-h-11 items-center">
                <input
                  type="checkbox"
                  checked={confirmed}
                  disabled={cash.busy || !canOpen}
                  onChange={(e) => setConfirmed(e.target.checked)}
                />
                {t('counted.confirm')}
              </label>
              <button
                className="admin-button admin-button-primary min-h-11 w-full sm:w-auto disabled:opacity-50"
                disabled={
                  !current.canOpen ||
                  !canOpen ||
                  blocked ||
                  cash.busy ||
                  context.isFetching ||
                  !valid ||
                  !confirmed ||
                  (current.safeInitialization?.required && !emptySafeConfirmed) ||
                  (different && !note.trim())
                }
                onClick={() => void submit()}
              >
                {t('counted.submit')}
              </button>
            </>
          )}
        </>
      )}
      {cash.intent && !receipt && (
        <div className="space-y-2">
          <CashRecoveryNotice error={cash.error} />
          <p className="break-all">
            {t('counted.key')}: {cash.intent.operationKey}
          </p>
          <button className="btn min-h-11" disabled={cash.busy} onClick={() => void lookup()}>
            {t('counted.lookup')}
          </button>
          {!sharedOpen && !closedDay && cash.intent.stage === 'UNKNOWN' && cash.intent.body && (
            <button
              className="btn min-h-11"
              disabled={cash.busy || !canOpen}
              onClick={() => void submit(cash.intent!)}
            >
              {t('counted.retry')}
            </button>
          )}
          {cash.intent.stage === 'REJECTED' && (
            <button
              className="btn min-h-11"
              onClick={() => {
                cash.startNew();
                setConfirmed(false);
                setEmptySafeConfirmed(false);
                void context.refetch();
              }}
            >
              {t('counted.startAgain')}
            </button>
          )}
        </div>
      )}
      {cash.error && <p role="alert">{t('counted.failed')}</p>}
      {receipt && (
        <div role="status" className="space-y-2">
          {receipt.businessDate !== current?.businessDate && (
            <p>{t('counted.historicalResult', { date: receipt.businessDate })}</p>
          )}
          <p>{t(`counted.result.${receipt.reviewStatus}`)}</p>
          {receipt.counts.map((c) => (
            <p key={c.kind}>
              {t(`cashWorkflow.bags.${c.kind}`)}: {c.amount} ·{' '}
              {t('counted.variance', { amount: c.variance })}
              {c.comparisonVariance !== null && (
                <> · {t('counted.comparisonVariance', { amount: c.comparisonVariance })}</>
              )}
            </p>
          ))}
          {receipt.reviewNote && (
            <p>
              {t('counted.reviewNote')}: {receipt.reviewNote}
            </p>
          )}
          <p className="break-all">
            {t('counted.key')}: {receipt.operationKey}
          </p>
          <Link className="btn min-h-11" to="/accounting/expenses">
            {t('goToExpenses')}
          </Link>
        </div>
      )}
      {cash.historical.length > 0 && (
        <details>
          <summary className="cursor-pointer min-h-11">{t('counted.historyTitle')}</summary>
          {cash.historical.map((record) => (
            <div key={record.operationKey} className="space-y-2 py-2">
              <p className="break-all">
                {t('counted.key')}: {record.operationKey}
              </p>
              <Link
                className="btn min-h-11"
                to={`/accounting/report?view=history&date=${record.businessDate}`}
              >
                {t('counted.historyDay', { date: record.businessDate })}
              </Link>
              <button
                className="btn min-h-11"
                disabled={cash.busy}
                onClick={() => void readHistory(record)}
              >
                {t('counted.lookup')}
              </button>
            </div>
          ))}
        </details>
      )}
      {!cash.intent && !currentReceipt && (
        <details>
          <summary className="cursor-pointer min-h-11">{t('counted.findPrevious')}</summary>
          <label>
            {t('counted.key')}
            <input
              className="input input-bordered w-full"
              value={lookupKey}
              onChange={(e) => setLookupKey(e.target.value)}
            />
          </label>
          <button
            className="btn min-h-11"
            disabled={cash.busy || !lookupKey.trim()}
            onClick={() => void lookup()}
          >
            {t('counted.lookup')}
          </button>
        </details>
      )}
    </section>
  );
}
